import { useState } from 'react';
import { Award, TrendingUp } from 'lucide-react';
import { Select } from '@/components/ui/Select';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { gradeFor, gradeToneClasses } from '@/constants/grading';
import type { Option } from '@/constants/institute';
import type { OverviewProgressEntry } from '@/types/teacherDashboard';
import { getNameInitials } from '@/lib/utils';

interface StudentProgressCardProps {
  /** The children leading the papers this teacher has marked, best first. */
  progress: { thisMonth: OverviewProgressEntry[]; thisTerm: OverviewProgressEntry[] };
  /**
   * The overview's day, `YYYY-MM-DD`. `thisMonth` is the month that day falls
   * in — not always the month we are in — so the option is named after it.
   */
  date?: string;
  isLoading: boolean;
}

type Timeframe = 'this_month' | 'this_term';

/** "November 2026". Read in UTC, as the API's dates are. */
function monthName(day: string): string {
  return new Date(`${day}T00:00:00.000Z`).toLocaleDateString('en-IN', {
    timeZone: 'UTC',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * The score drawn in its grade band's colour.
 *
 * Banded on the score rather than sent by the server: it is a way of drawing a
 * number, not a fact about the child. The shared scale rather than a cut of its
 * own, so a 62% here is the same "B" the marks screens call it.
 */
function badgeClass(percentage: number): string {
  return `border ${gradeToneClasses(gradeFor(percentage, 100)).badge}`;
}

export function StudentProgressCard({ progress, date, isLoading }: StudentProgressCardProps) {
  const [timeframe, setTimeframe] = useState<Timeframe>('this_month');

  const monthLabel = date ? monthName(date) : 'This month';
  // `thisTerm` is the academic session so far, not a term within it.
  const timeframeOptions: Option<Timeframe>[] = [
    { value: 'this_month', label: monthLabel },
    { value: 'this_term', label: 'This session' },
  ];

  const students = timeframe === 'this_month' ? progress.thisMonth : progress.thisTerm;

  return (
    <div className="card bg-surface border border-line rounded-[20px] p-5 shadow-2xs flex flex-col gap-4">
      {/* Header with Dropdown matching image */}
      <div className="flex items-center justify-between border-b border-line pb-3">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4.5 w-4.5 text-accent" />
          <h3 className="text-base font-bold text-ink tracking-tight">Student Progress</h3>
        </div>

        <Select
          id="student-progress-timeframe"
          label="Choose the period to rank by"
          hideLabel
          options={timeframeOptions}
          value={timeframe}
          onChange={(event) => setTimeframe(event.target.value as Timeframe)}
          // Sized down to the card header; `Select` has no `sm` the way
          // `DatePicker` does, and the trigger merges `className`.
          className="h-8 w-36 px-2.5 text-xs"
        />
      </div>

      {/* Student List Rows matching image */}
      {isLoading ? (
        <SkeletonLines count={3} />
      ) : students.length === 0 ? (
        <p className="py-6 text-center text-sm text-ink-3 font-medium">
          {timeframe === 'this_month'
            ? `No marks entered for ${date ? monthLabel : 'this month'} yet.`
            : 'No marks entered this session yet.'}
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {students.map((student) => (
            <div
              key={student.uuid}
              className="p-3 rounded-[14px] bg-surface-2/40 border border-line flex items-center justify-between gap-3 hover:border-accent/30 transition-all"
            >
              {/* Left: Initials + Name + Class */}
              <div className="flex items-center gap-3">
                <span
                  aria-hidden
                  className="grid h-10 w-10 place-items-center rounded-[10px] bg-accent/15 text-accent text-xs font-black border border-line shadow-2xs"
                >
                  {getNameInitials(student.fullName)}
                </span>
                <div>
                  <h4 className="text-xs font-bold text-ink">{student.fullName}</h4>
                  <p className="text-3xs text-ink-3 font-medium">{student.displayName}</p>
                </div>
              </div>

              {/* Right: Medal icon + Percentage badge */}
              <div className="flex items-center gap-2">
                {student.percentage >= 90 && (
                  <span className="grid h-7 w-7 place-items-center rounded-full bg-blue-500/10 text-blue-600 border border-blue-500/20">
                    <Award className="h-4 w-4" />
                  </span>
                )}
                <span
                  className={`px-2.5 py-1 rounded-[8px] text-xs font-black font-mono shadow-2xs ${badgeClass(student.percentage)}`}
                >
                  {student.percentage}%
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
